import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import {
  Box,
  Button,
  Checkbox,
  Group,
  type MantineTheme,
  Menu,
  Paper,
  SegmentedControl,
  Skeleton,
  Text,
  Title,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { IconSettings } from '@tabler/icons-react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { usdFormatter } from '@repo/common/helpers/formatters'
import {
  type FundAssetNavsResponse,
  type FundNavsResponse,
  useAdminFundAssetNavs,
  useAdminFundNavs,
} from '@repo/common/queries/admin/fund'

import { TowerCard } from '@/components/ui/TowerCard'

import classes from './NavChart.module.css'

dayjs.extend(utc)
dayjs.extend(timezone)

const tz = 'America/New_York'

const formatShortNumber = (value: number): string => {
  if (value === 0) return '$0'

  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue >= 1_000_000_000) {
    return `${sign}$${(value / 1_000_000_000).toFixed(3)}B`
  }
  if (absValue >= 1_000_000) {
    return `${sign}$${(value / 1_000_000).toFixed(3)}M`
  }
  if (absValue >= 1_000) {
    return `${sign}$${(value / 1_000).toFixed(3)}K`
  }

  return usdFormatter(value)
}

const COLORS = [
  '#0088FE', // blue
  '#00C49F', // teal
  '#FFBB28', // yellow
  '#FF8042', // orange
  '#A28FD0', // purple
  '#FF6B6B', // red
  '#54A0FF', // light blue
  '#5D9CEC', // periwinkle
  '#4BC0C0', // turquoise
  '#FFA726', // amber
  '#66BB6A', // green
  '#EC407A', // pink
  '#7E57C2', // deep purple
  '#78909C', // blue grey
  '#26A69A', // teal accent
  '#EF5350', // red accent
]

type ChartMode = 'NAV' | 'PPS'
type SeriesType = 'fund' | 'feeder' | 'company'

interface ChartSeries {
  id: string
  type: SeriesType
  name: string
  color: string
  dataKey: string
  yAxisId: 'left' | 'right'
  companyId?: string
}

interface CombinedDataPoint {
  date: string
  published: boolean
  [key: string]: string | number | boolean | undefined
}

export interface NavChartRef {
  refresh: () => Promise<void>
}

interface NavChartProps {
  fundId: string
  defaultMode?: ChartMode
  height?: number
  width?: string | number
  initialSeries?: string[]
  selectedDate?: string
  onSelectDate?: ((date: string) => void) | true
  startDate?: string
  endDate?: string
  onRangeChange?: (startDate: string, endDate: string) => void
}

const formatDate = (dateString: string) => {
  return dayjs(dateString).format("MMM D, 'YY")
}

function buildSeries(
  fundNavData: FundNavsResponse | undefined,
  theme: MantineTheme,
  assetNavData: FundAssetNavsResponse | undefined,
) {
  const series: ChartSeries[] = []

  if (fundNavData) {
    series.push({
      id: 'fund',
      type: 'fund',
      name: `${fundNavData.name} NAV`,
      color: theme.colors.blue![6],
      dataKey: 'fund_nav',
      yAxisId: 'left',
    })

    fundNavData.feeders.forEach((feeder, index) => {
      series.push({
        id: `feeder_${feeder.id}`,
        type: 'feeder',
        name: `${feeder.name} (${feeder.market})`,
        color: COLORS[index % COLORS.length]!,
        dataKey: `feeder_${feeder.id}`,
        yAxisId: 'left',
      })
    })
  }

  if (assetNavData) {
    assetNavData.assets.nodes.forEach((companyNode, index) => {
      const company = companyNode.asset
      const colorIndex =
        (index + (fundNavData?.feeders.length || 0)) % COLORS.length
      series.push({
        id: `company_${company.id}`,
        type: 'company',
        name: company.name,
        color: COLORS[colorIndex]!,
        dataKey: `company_${company.id}`,
        yAxisId: 'right',
        companyId: company.id,
      })
    })
  }

  return series
}

/** requires navs to be sorted by published_at ascending */
function sampleNavAt<
  T extends { published_at: string | null; effective_at: string },
>(at: dayjs.Dayjs, navs: T[]): T | null {
  let bestNav: T | null = null

  for (const nav of navs) {
    if (nav.published_at === null) continue

    const navTime = dayjs(nav.published_at)
    if (navTime.valueOf() <= at.valueOf()) {
      bestNav = nav
    } else {
      break
    }
  }

  if (bestNav) {
    return Object.assign({}, bestNav, {
      published_at: at.toISOString(),
    })
  }

  return null
}

function sampleDateRange<
  T extends { published_at: string | null; effective_at: string },
>(navs: T[], startDateOpt?: string, endDateOpt?: string): T[] {
  const published = navs.filter((nav) => nav.published_at !== null)
  if (published.length === 0) return []

  const sortedNavs = published.sort(
    (a, b) => dayjs(a.published_at).valueOf() - dayjs(b.published_at).valueOf(),
  )

  if (!sortedNavs.every((val, idx) => val === published[idx])) {
    console.warn('sampleDailyData: navs were not sorted by published_at')
  }

  const firstNav = sortedNavs[0]!
  const lastNav = sortedNavs[sortedNavs.length - 1]!

  const startDate =
    startDateOpt || dayjs(firstNav.published_at).format('YYYY-MM-DD')
  const endDate = endDateOpt || dayjs(lastNav.published_at).format('YYYY-MM-DD')
  const sampledNavs: T[] = []
  let currentDate = startDate
  let max = 1000
  while (currentDate <= endDate) {
    const currentDateTs = dayjs(currentDate).tz(tz).startOf('day')
    const nav = sampleNavAt(currentDateTs.endOf('day'), sortedNavs)
    if (nav) {
      sampledNavs.push(nav)
    }
    currentDate = currentDateTs.add(1, 'day').format('YYYY-MM-DD')
    max -= 1
    if (max <= 0) {
      console.warn('sampleDateRange: reached max iterations, breaking loop')
      break
    }
  }
  return sampledNavs
}

export const NavChart = forwardRef<NavChartRef, NavChartProps>(
  (
    {
      fundId,
      defaultMode = 'NAV',
      height = 400,
      width = '100%',
      initialSeries = ['fund'],
      selectedDate: externalSelectedDate,
      onSelectDate,
      startDate: externalStartDate,
      endDate: externalEndDate,
    },
    ref,
  ) => {
    const startDate = externalStartDate
    const endDate = externalEndDate

    const {
      data: fundNavData,
      isLoading: fundNavLoading,
      error: fundNavError,
      refetch: refetchFundNav,
    } = useAdminFundNavs({ fundId, startDate, endDate })

    const {
      data: companyNavData,
      isLoading: companyNavLoading,
      error: companyNavError,
      refetch: refetchCompanyNav,
    } = useAdminFundAssetNavs({ fundId, startDate, endDate })

    const refresh = useCallback(async () => {
      await Promise.all([refetchFundNav(), refetchCompanyNav()])
    }, [refetchFundNav, refetchCompanyNav])

    useImperativeHandle(
      ref,
      () => ({
        refresh,
      }),
      [refresh],
    )

    const theme = useMantineTheme()
    const { colorScheme } = useMantineColorScheme()
    const isDark = colorScheme === 'dark'
    const availableSeries = useMemo(() => {
      if (!fundNavData || !companyNavData) return []
      return buildSeries(fundNavData, theme, companyNavData)
    }, [fundNavData, companyNavData, theme])
    const [visibleSeries, setVisibleSeries] = useState<Set<string>>(
      new Set(initialSeries),
    )
    const [chartMode, setChartMode] = useState<ChartMode>(defaultMode)
    const [displayMode, setDisplayMode] = useState<'chart' | 'table'>('chart')
    const [menuOpened, setMenuOpened] = useState(false)
    const [hoveredSeries, setHoveredSeries] = useState<string | null>(null)
    const [selectedDate, setSelectedDate] = useState<string | undefined>(
      externalSelectedDate,
    )
    // let selectedDate = externalSelectedDate

    useEffect(() => {
      setSelectedDate(externalSelectedDate)
    }, [externalSelectedDate])

    const toggleSeries = (seriesId: string, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation()
      }

      setVisibleSeries((all) => {
        const newSet = new Set(all)
        if (newSet.has(seriesId)) {
          newSet.delete(seriesId)
        } else {
          newSet.add(seriesId)
        }
        return newSet
      })
    }

    const toggleAllSeriesOfType = (
      type: SeriesType,
      event?: React.MouseEvent,
    ) => {
      if (event) {
        event.stopPropagation()
      }

      setVisibleSeries((all) => {
        const newSet = new Set(all)
        availableSeries.forEach((s) => {
          if (s.type === type) {
            if (newSet.has(s.id)) {
              newSet.delete(s.id)
            } else {
              newSet.add(s.id)
            }
          }
        })
        return newSet
      })
    }

    const chartData: CombinedDataPoint[] = useMemo(() => {
      if (!fundNavData && !companyNavData) return []
      const dateMap = new Map<string, CombinedDataPoint>()

      if (fundNavData) {
        const sampledNavs = sampleDateRange(
          fundNavData.navs.nodes,
          startDate,
          endDate,
        )

        sampledNavs.forEach((nav) => {
          const dateKey = dayjs(nav.published_at).tz(tz).format('YYYY-MM-DD')
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, { date: dateKey, published: true })
          }
          const dataPoint = dateMap.get(dateKey)!
          if (chartMode === 'NAV') {
            dataPoint.fund_nav = nav.valuation
          } else {
            dataPoint.fund_nav = nav.price_per_share
          }
        })

        fundNavData.feeders.forEach((feeder) => {
          const feederSampledNavs = sampleDateRange(
            feeder.navs.nodes,
            startDate,
            endDate,
          )

          feederSampledNavs.forEach((nav) => {
            const dateKey = dayjs(nav.published_at).tz(tz).format('YYYY-MM-DD')
            if (!dateMap.has(dateKey)) {
              dateMap.set(dateKey, { date: dateKey, published: true })
            }
            const dataPoint = dateMap.get(dateKey)!
            if (chartMode === 'NAV') {
              dataPoint[`feeder_${feeder.id}`] = nav.valuation
            } else {
              dataPoint[`feeder_${feeder.id}`] = nav.price_per_share
            }
          })
        })
      }

      if (companyNavData) {
        companyNavData.assets.nodes.forEach((companyNode) => {
          const companyId = companyNode.asset.id
          const companySampledNavs = sampleDateRange(
            companyNode.navs.nodes,
            startDate,
            endDate,
          )

          companySampledNavs.forEach((nav) => {
            const dateKey = dayjs(nav.published_at).tz(tz).format('YYYY-MM-DD')
            if (!dateMap.has(dateKey)) {
              dateMap.set(dateKey, { date: dateKey, published: true })
            }
            const dataPoint = dateMap.get(dateKey)!

            if (chartMode === 'NAV') {
              dataPoint[`company_${companyId}`] = nav.shares_value_held || 0
            } else {
              dataPoint[`company_${companyId}`] =
                nav.shares_held > 0
                  ? (nav.shares_value_held || 0) / nav.shares_held
                  : 0
            }
          })
        })
      }

      return Array.from(dateMap.values()).sort(
        (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
      )
    }, [fundNavData, companyNavData, chartMode, startDate, endDate])

    if (fundNavLoading || companyNavLoading) {
      return (
        <TowerCard title="NAV Chart" description="Loading data...">
          <Box className={classes.loading_container}>
            <Skeleton height={height} width="100%" radius="md" />
          </Box>
        </TowerCard>
      )
    }

    if (fundNavError || companyNavError || (!fundNavData && !companyNavData)) {
      return (
        <Paper
          shadow="md"
          p="lg"
          radius="md"
          withBorder
          className={classes.container}
        >
          <Title order={3}>NAV Chart</Title>
          <Text c="red">Error loading NAV data</Text>
        </Paper>
      )
    }

    const fundSeries = availableSeries.filter((s) => s.type === 'fund')
    const feederSeries = availableSeries.filter((s) => s.type === 'feeder')
    const companySeries = availableSeries.filter((s) => s.type === 'company')

    const hasVisibleCompanies = availableSeries.some(
      (s) => s.type === 'company' && visibleSeries.has(s.id),
    )
    const hasVisibleFundOrFeeders = availableSeries.some(
      (s) =>
        (s.type === 'fund' || s.type === 'feeder') && visibleSeries.has(s.id),
    )

    return (
      <TowerCard
        title={`${fundNavData?.name || 'Fund'} ${chartMode} Chart`}
        description={
          chartMode === 'NAV'
            ? 'Fund valuation and company holdings'
            : 'Price per share trends'
        }
      >
        <Box className={classes.chart_controls}>
          <Group justify="space-between">
            <Group>
              <SegmentedControl
                value={chartMode}
                onChange={(value) => {
                  setChartMode(value as ChartMode)
                }}
                data={[
                  { label: 'NAV', value: 'NAV' },
                  { label: 'PPS', value: 'PPS' },
                ]}
              />

              <SegmentedControl
                value={displayMode}
                onChange={(value) => {
                  setDisplayMode(value as 'chart' | 'table')
                }}
                data={[
                  { label: 'Chart', value: 'chart' },
                  { label: 'Table', value: 'table' },
                ]}
              />

              <Menu
                opened={menuOpened}
                onChange={setMenuOpened}
                position="bottom-start"
                width={300}
              >
                <Menu.Target>
                  <Button
                    leftSection={<IconSettings size={16} />}
                    variant="light"
                  >
                    Select Series
                  </Button>
                </Menu.Target>

                <Menu.Dropdown className={classes.series_menu}>
                  {/* Fund series */}
                  <div className={classes.series_group}>
                    <Menu.Label>Fund</Menu.Label>
                    {fundSeries.map((series) => (
                      <Menu.Item
                        key={series.id}
                        className={classes.series_item}
                        onClick={(event) => {
                          toggleSeries(series.id, event)
                        }}
                        closeMenuOnClick={false}
                      >
                        <Checkbox
                          checked={visibleSeries.has(series.id)}
                          onChange={() => {}}
                          label={
                            <Group>
                              <span
                                className={classes.color_indicator}
                                style={{ backgroundColor: series.color }}
                              />
                              <Text size="sm">{series.name}</Text>
                            </Group>
                          }
                        />
                      </Menu.Item>
                    ))}
                  </div>

                  {/* Feeder series */}
                  {feederSeries.length > 0 && (
                    <div className={classes.series_group}>
                      <Menu.Label>
                        <Group justify="space-between">
                          <span>Feeders</span>
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={(event) => {
                              toggleAllSeriesOfType('feeder', event)
                            }}
                          >
                            Toggle All
                          </Button>
                        </Group>
                      </Menu.Label>
                      {feederSeries.map((series) => (
                        <Menu.Item
                          key={series.id}
                          className={classes.series_item}
                          onClick={(event) => {
                            toggleSeries(series.id, event)
                          }}
                          closeMenuOnClick={false}
                        >
                          <Checkbox
                            checked={visibleSeries.has(series.id)}
                            onChange={() => {}}
                            label={
                              <Group>
                                <span
                                  className={classes.color_indicator}
                                  style={{ backgroundColor: series.color }}
                                />
                                <Text size="sm">{series.name}</Text>
                              </Group>
                            }
                          />
                        </Menu.Item>
                      ))}
                    </div>
                  )}

                  {/* Company series */}
                  {companySeries.length > 0 && (
                    <div className={classes.series_group}>
                      <Menu.Label>
                        <Group justify="space-between">
                          <span>Companies</span>
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={(event) => {
                              toggleAllSeriesOfType('company', event)
                            }}
                          >
                            Toggle All
                          </Button>
                        </Group>
                      </Menu.Label>
                      {companySeries.map((series) => (
                        <Menu.Item
                          key={series.id}
                          className={classes.series_item}
                          onClick={(event) => {
                            toggleSeries(series.id, event)
                          }}
                          closeMenuOnClick={false}
                        >
                          <Checkbox
                            checked={visibleSeries.has(series.id)}
                            onChange={() => {}}
                            label={
                              <Group>
                                <span
                                  className={classes.color_indicator}
                                  style={{ backgroundColor: series.color }}
                                />
                                <Text size="sm">{series.name}</Text>
                              </Group>
                            }
                          />
                        </Menu.Item>
                      ))}
                    </div>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>

            <Group>
              {onSelectDate && (
                <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                  Click on the chart to select a date
                </Text>
              )}
            </Group>
          </Group>
        </Box>

        {displayMode === 'chart' ? (
          <Box className={classes.chart_container} style={{ height }}>
            <ResponsiveContainer width={width} height="100%">
              <LineChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                onClick={(data) => {
                  if (
                    data.activeLabel &&
                    onSelectDate &&
                    onSelectDate !== true
                  ) {
                    onSelectDate(data.activeLabel)
                  } else if (onSelectDate === true) {
                    setSelectedDate(data.activeLabel)
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  minTickGap={40}
                />

                <YAxis
                  yAxisId="left"
                  orientation="left"
                  domain={['auto', 'auto']}
                  tickFormatter={(value: number) => formatShortNumber(value)}
                  hide={!hasVisibleFundOrFeeders}
                />

                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={['auto', 'auto']}
                  tickFormatter={(value: number) => formatShortNumber(value)}
                  hide={!hasVisibleCompanies}
                />

                <Tooltip
                  formatter={(value: number, name: string) => {
                    const series = availableSeries.find(
                      (s) => s.dataKey === name,
                    )
                    let displayName = name
                    if (series) {
                      displayName = series.name
                    }
                    return [usdFormatter(value), displayName]
                  }}
                  labelFormatter={formatDate}
                  contentStyle={{
                    backgroundColor: isDark
                      ? theme.colors.dark![7]
                      : theme.white,
                    borderColor: isDark
                      ? theme.colors.dark![5]
                      : theme.colors.gray![2],
                    color: isDark ? theme.white : theme.black,
                  }}
                  itemStyle={{
                    color: isDark ? theme.white : theme.black,
                  }}
                />

                <Legend
                  content={(props) => {
                    const { payload } = props
                    if (!payload) return null

                    return (
                      <div className={classes.legend_container}>
                        {payload.map((entry, index: number) => {
                          const seriesName = entry.dataKey
                          const isHovered = hoveredSeries === seriesName
                          const otherSeriesHovered =
                            hoveredSeries !== null &&
                            hoveredSeries !== seriesName
                          const className = `${classes.legend_item} ${otherSeriesHovered ? classes.inactive_legend_item : ''}`

                          if (!seriesName) return

                          return (
                            <span
                              key={`item-${index}`}
                              className={className}
                              onMouseEnter={() => {
                                setHoveredSeries(seriesName as string)
                              }}
                              onMouseLeave={() => {
                                setHoveredSeries(null)
                              }}
                              onClick={() => {
                                toggleSeries(
                                  availableSeries.find(
                                    (s) => s.dataKey === seriesName,
                                  )?.id || '',
                                )
                              }}
                            >
                              <span
                                className={`${classes.legend_color_dot} ${
                                  isHovered
                                    ? classes.legend_color_dot_hovered
                                    : classes.legend_color_dot_normal
                                }`}
                                style={{ backgroundColor: entry.color }}
                              />
                              <span>{entry.value}</span>
                            </span>
                          )
                        })}
                      </div>
                    )
                  }}
                />

                {chartMode === 'PPS' &&
                  fundNavData?.starting_price_per_share && (
                    <ReferenceLine
                      y={fundNavData.starting_price_per_share}
                      yAxisId="left"
                      label="Starting Price"
                      stroke="#888"
                      strokeDasharray="3 3"
                    />
                  )}

                {selectedDate && (
                  <ReferenceLine
                    x={selectedDate}
                    stroke={theme.colors.blue![9]}
                    strokeWidth={2}
                    yAxisId="left"
                    label={{
                      value: 'Selected Date',
                      position: 'top',
                      fill: theme.colors.blue![9],
                    }}
                  />
                )}

                {availableSeries
                  .filter((series) => visibleSeries.has(series.id))
                  .map((series) => {
                    const strokeDasharray = 'none'
                    let strokeWidth = 2

                    const isHovered = hoveredSeries === series.dataKey
                    const otherSeriesHovered =
                      hoveredSeries !== null && hoveredSeries !== series.dataKey

                    if (isHovered) {
                      strokeWidth += 1
                    } else if (otherSeriesHovered) {
                      strokeWidth *= 0.75
                    }

                    const renderDot = (props: {
                      cx: number
                      cy: number
                      payload: CombinedDataPoint
                    }) => {
                      const { cx, cy, payload } = props
                      const isSelected = selectedDate === payload.date
                      let dotRadius = series.type === 'fund' ? 4 : 2
                      if (isSelected) {
                        dotRadius = series.type === 'fund' ? 8 : 4
                      }
                      if (isHovered) {
                        dotRadius += 1
                      } else if (otherSeriesHovered) {
                        dotRadius *= 0.75
                      }

                      const fillColor = series.color
                      const dotStrokeWidth = isSelected ? 4 : isHovered ? 3 : 0
                      const strokeColor = '#fff'

                      return (
                        <circle
                          key={`${series.id}-dot-${payload.date}`}
                          cx={cx}
                          cy={cy}
                          r={dotRadius}
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth={dotStrokeWidth}
                          opacity={otherSeriesHovered ? 0.5 : 1}
                        />
                      )
                    }

                    return (
                      <Line
                        key={series.id}
                        yAxisId={series.yAxisId}
                        type="monotone"
                        dataKey={series.dataKey}
                        name={series.name}
                        stroke={series.color}
                        activeDot={{ r: 1 }}
                        dot={renderDot}
                        strokeWidth={strokeWidth}
                        strokeDasharray={strokeDasharray}
                        connectNulls
                        isAnimationActive={true}
                        opacity={otherSeriesHovered ? 0.5 : 1}
                      />
                    )
                  })}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box className={classes.table_container}>
            <div className={classes.table_scroll_container}>
              <table className={classes.data_table}>
                <thead>
                  <tr>
                    <th className={classes.date_column}>Date</th>
                    {availableSeries
                      .filter((series) => visibleSeries.has(series.id))
                      .map((series) => (
                        <th
                          key={series.id}
                          className={classes.value_column}
                          style={{
                            borderBottom: `2px solid ${series.color}`,
                            color: series.color,
                          }}
                        >
                          {series.name}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((dataPoint) => (
                    <tr
                      key={dataPoint.date}
                      className={
                        selectedDate === dataPoint.date
                          ? classes.selected_row
                          : ''
                      }
                      onClick={() => {
                        if (onSelectDate && onSelectDate !== true) {
                          onSelectDate(dataPoint.date)
                        } else if (onSelectDate === true) {
                          setSelectedDate(dataPoint.date)
                        }
                      }}
                    >
                      <td className={classes.date_column}>
                        {formatDate(dataPoint.date)}
                      </td>
                      {availableSeries
                        .filter((series) => visibleSeries.has(series.id))
                        .map((series) => {
                          const value = dataPoint[series.dataKey] as
                            | number
                            | undefined
                          return (
                            <td
                              key={series.id}
                              className={classes.value_column}
                            >
                              {value !== undefined ? usdFormatter(value) : '-'}
                            </td>
                          )
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Box>
        )}
      </TowerCard>
    )
  },
)

NavChart.displayName = 'NavChart'
